import _ from "underscore";
import React, {useState, useCallback, useMemo, useEffect} from "react";
import styled from "styled-components";
import {Link} from "react-router";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";

import applyWorkflowTaskOverrides from "routes/llm_run/utils/apply_workflow_task_overrides";
import Fieldset from "../styled/fieldset";
import Legend from "../styled/legend";
import HeadingCell from "../styled/heading_cell";
import JSONPrinter from "./json_printer";
import Arguments from "./arguments";

import {OrganisationId} from "common/types/organisation";
import {EnhancedLlmRun} from "common/flowmaster/types/llm_run";
import {
  ActionDefinitionWithType,
  TaskArgument,
  WorkflowInputValuesArgument,
} from "common/flowmaster/types/task_config";
import {ActionOutputMeta, Prompt} from "common/flowmaster/types/workflow";
import {
  StateHistoryItem,
  WorkflowTaskStateItem,
} from "common/flowmaster/types/workflow_state";

interface ActionRowProps {
  $isExecuteWorkflow: boolean;
  $isEven: boolean;
  $isCollapsed?: boolean;
}

const TableRow = styled.tr`
  vertical-align: top;
  border-bottom: 1px solid #ccc;
  & th,
  & td {
    padding: 0.25em 1em;
  }
  & th:not(:last-child),
  & td:not(:last-child) {
    border-right: 1px solid #ddd;
  }
`;
const ActionHeaderRow = styled(TableRow)`
  position: sticky;
  top: 4em;
  background-color: white;
  z-index: 10000;
`;
const ActionRow = styled(TableRow)<ActionRowProps>`
  background-color: ${({$isExecuteWorkflow, $isEven}) =>
    $isExecuteWorkflow ? "#f5f5b8" : $isEven ? "#fcfcfc" : "#eef"};
`;

const ToggleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const NameCell = styled.td`
  cursor: pointer;
`;

const BaseTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  > thead > tr > th,
  > tbody > tr > td {
    padding: 0.5em 0.25em;
    word-break: break-word;
    overflow: hidden;
    width: 4em;
  }
`;

const DetailTable = styled(BaseTable)`
  > thead > tr > th,
  > tbody > tr > td {
    &:nth-of-type(1) {
      width: 2em;
    }
    &:nth-of-type(8) {
      width: 15%;
    }
    &:nth-of-type(9) {
      width: 30%;
    }
    &:nth-of-type(10) {
      width: 55%;
    }
  }
`;

const SummaryTable = styled(BaseTable)`
  > thead > tr > th,
  > tbody > tr > td {
    &:nth-of-type(1) {
      width: 6em;
    }
    &:nth-of-type(2) {
      width: 3em;
    }
    &:nth-of-type(3) {
      width: 50%;
    }
    &:nth-of-type(4) {
      width: 50%;
    }
  }
`;

const LowImpact = styled.span`
  font-family: monospace;
  color: #888;
  font-size: 0.9em;
`;

const PlainLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline #ddd;
  }
`;

const ExpandableCell = styled.td<{$isCollapsed: boolean}>`
  & > div {
    display: ${props => (props.$isCollapsed ? "none" : "block")};
  }
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ToggleButton = styled.button`
  background-color: #f8f8f8;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  color: #333;
  padding: 0.35em 0.75em;
  margin-left: 0.5rem;
`;

const AUTO_COLLAPSE_THRESHOLD = 20;

interface Props {
  llmRun: EnhancedLlmRun;
  actionDefinitions: ActionDefinitionWithType[];
  prompts: Prompt[];
  showInternalActions: boolean;
  showActionDetail: boolean;
  organisationId: OrganisationId;
}

function formatTime(date) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

function shouldAutoCollapse(statesPerformed) {
  return statesPerformed.length >= AUTO_COLLAPSE_THRESHOLD;
}

type RowData = StateHistoryItem & {runInfo: WorkflowTaskStateItem};

export default function Actions({
  llmRun,
  actionDefinitions,
  prompts,
  showInternalActions,
  showActionDetail,
  organisationId,
}: Props) {
  const tasks = useMemo(() => {
    return !_.isEmpty(llmRun.overrides)
      ? applyWorkflowTaskOverrides(llmRun.tasks, llmRun.overrides)
      : llmRun.tasks;
  }, [llmRun.overrides, llmRun.tasks]);
  const combinedData: RowData[] = useMemo(
    () =>
      llmRun.data.states_performed.map((statePerformed, index) => ({
        ...statePerformed,
        runInfo: llmRun.all_task_data[index],
      })),
    [llmRun.data.states_performed, llmRun.all_task_data],
  );

  const statesPerformed = useMemo(() => {
    return combinedData.filter(item => {
      const action = actionDefinitions.find(
        action => action.key === item.action,
      );
      return (
        showInternalActions ||
        (item.action !== "start" &&
          action !== undefined &&
          action.type !== "internal")
      );
    });
  }, [combinedData, actionDefinitions, showInternalActions]);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const [allCollapsed, setAllCollapsed] = useState(
    shouldAutoCollapse(statesPerformed),
  );

  useEffect(() => {
    setAllCollapsed(shouldAutoCollapse(statesPerformed));
  }, [statesPerformed]);

  const toggleRowCollapse = useCallback(id => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const isRowCollapsed = useCallback(
    id => {
      return allCollapsed ? !expandedRows.has(id) : expandedRows.has(id);
    },
    [allCollapsed, expandedRows],
  );

  const toggleAllRows = useCallback(() => {
    setAllCollapsed(prev => !prev);
    setExpandedRows(new Set());
  }, []);

  const Table = showActionDetail ? DetailTable : SummaryTable;

  return (
    <Fieldset>
      <Legend>
        <LegendContainer>
          <span>Actions ({statesPerformed.length})</span>
          <ToggleButton onClick={toggleAllRows}>
            {allCollapsed ? "Open All" : "Close All"}
          </ToggleButton>
        </LegendContainer>
      </Legend>

      <Table>
        <thead>
          <ActionHeaderRow>
            {showActionDetail && <HeadingCell>key</HeadingCell>}
            <HeadingCell>name</HeadingCell>
            {showActionDetail && <HeadingCell>start</HeadingCell>}
            {showActionDetail && <HeadingCell>end</HeadingCell>}
            {showActionDetail && <HeadingCell>action</HeadingCell>}
            <HeadingCell>cost</HeadingCell>
            {showActionDetail && <HeadingCell>tokens</HeadingCell>}
            {showActionDetail && <HeadingCell>meta</HeadingCell>}
            <HeadingCell>output</HeadingCell>
            <HeadingCell>args</HeadingCell>
          </ActionHeaderRow>
        </thead>
        <tbody>
          {statesPerformed.map((item, index) => {
            const task = tasks.find(task => task.id === item.id);
            const action = actionDefinitions.find(
              action => action.key === item.action,
            );

            if (
              !action ||
              (action?.type === "internal" && !showInternalActions)
            ) {
              return null;
            }
            let meta =
              item.runInfo?.data?.meta ?? ({} as Record<string, unknown>);
            if ("cost_detail" in meta) {
              meta = {
                ...(_.omit(meta, ["cost_detail"]) as ActionOutputMeta),
                ...(meta["cost_detail"] as Record<string, number>),
              };
            }

            const listWorkflowId =
              item.action === "execute_workflow_on_list"
                ? task?.args?.find(
                    (arg): arg is WorkflowInputValuesArgument =>
                      typeof arg.value === "object" &&
                      "workflowId" in arg.value,
                  )?.value?.workflowId
                : null;

            const rowKey = `${item.id}-${index}`;
            const collapsed = isRowCollapsed(rowKey);

            return (
              <ActionRow
                key={rowKey}
                $isExecuteWorkflow={action.key === "execute_workflow_inline"}
                $isEven={index % 2 === 0}
                $isCollapsed={collapsed}
              >
                {showActionDetail && task && (
                  <td>
                    <ToggleIcon onClick={() => toggleRowCollapse(rowKey)}>
                      {collapsed ? (
                        <KeyboardArrowRightIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </ToggleIcon>
                    <PlainLink
                      to={`/organisation/${organisationId}/workflow/${
                        llmRun.task_workflow_mapping[task.id]
                      }/detail?task=${task.id}`}
                    >
                      <LowImpact>{task?.id}</LowImpact>
                    </PlainLink>
                  </td>
                )}
                <NameCell onClick={() => toggleRowCollapse(rowKey)}>
                  {!showActionDetail && (
                    <ToggleIcon>
                      {collapsed ? (
                        <KeyboardArrowRightIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </ToggleIcon>
                  )}
                  {item.action === "execute_workflow_on_list" &&
                  listWorkflowId ? (
                    <PlainLink
                      to={`/organisation/${organisationId}/llm_run/list?parent=${llmRun.run_id}&workflow=${listWorkflowId}&from=${llmRun.creation_date}`}
                    >
                      {task?.name}
                    </PlainLink>
                  ) : (
                    task?.name
                  )}
                </NameCell>
                {showActionDetail && (
                  <ExpandableCell
                    $isCollapsed={collapsed}
                    title={item?.start_time}
                  >
                    <div>
                      <LowImpact>
                        {formatTime(new Date(item?.start_time))}
                      </LowImpact>
                    </div>
                  </ExpandableCell>
                )}
                {showActionDetail && (
                  <ExpandableCell
                    $isCollapsed={collapsed}
                    title={item?.end_time}
                  >
                    <div>
                      <LowImpact>
                        {formatTime(new Date(item?.end_time))}
                      </LowImpact>
                    </div>
                  </ExpandableCell>
                )}
                {showActionDetail && (
                  <ExpandableCell $isCollapsed={collapsed}>
                    <div>
                      <LowImpact>{item.action}</LowImpact>
                    </div>
                  </ExpandableCell>
                )}
                <ExpandableCell $isCollapsed={collapsed}>
                  <div>{meta?.cost}</div>
                </ExpandableCell>
                {showActionDetail && (
                  <ExpandableCell $isCollapsed={collapsed}>
                    <div>
                      <LowImpact>{meta?.total_tokens}</LowImpact>
                    </div>
                  </ExpandableCell>
                )}
                {showActionDetail && (
                  <ExpandableCell $isCollapsed={collapsed}>
                    <div>
                      {!collapsed && (
                        <JSONPrinter
                          json={{
                            ..._.pick(meta, [
                              "id",
                              "model",
                              "cost",
                              "input_cost",
                              "output_cost",
                              "cached_cost",
                              "input_tokens",
                              "output_tokens",
                              "cached_tokens",
                            ]),
                            ..._.omit(meta, [
                              "id",
                              "model",
                              "cost",
                              "input_cost",
                              "output_cost",
                              "cached_cost",
                              "input_tokens",
                              "output_tokens",
                              "cached_tokens",
                            ]),
                          }}
                          small
                        />
                      )}
                    </div>
                  </ExpandableCell>
                )}
                <ExpandableCell $isCollapsed={collapsed}>
                  <div>
                    {!collapsed && (
                      <JSONPrinter
                        json={_.omit(item?.runInfo?.data ?? {}, "meta")}
                      />
                    )}
                  </div>
                </ExpandableCell>
                <ExpandableCell
                  $isCollapsed={collapsed}
                  style={{width: "100%"}}
                >
                  <div>
                    {!collapsed && (
                      <Arguments
                        action={action}
                        args={task?.args as TaskArgument[]}
                        llmRun={llmRun}
                        prompts={prompts}
                        showInternalActions={showInternalActions}
                      />
                    )}
                  </div>
                </ExpandableCell>
              </ActionRow>
            );
          })}
        </tbody>
      </Table>
    </Fieldset>
  );
}
